import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection, getDocs, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp, query, orderBy, limit, writeBatch, where, startAfter, increment,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage, firebaseConfigValid } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { parseExcelFile } from '@/utils/excelParser';
import { WORKFLOW_STATES, canTransition } from '@/lib/workflow';
import { writeAuditLog, AUDIT_ACTIONS } from '@/lib/auditLog';
import { filterPractices } from '@/utils/practiceFilters';
import { ROLES } from '@/lib/roles';
import { canSeePractice } from '@/lib/territorio';

// Stati che la Ditta può vedere (solo dopo che HQ ha approvato i documenti).
// Allineato a firestore.rules → /practices read per role='ditta'.
const DITTA_VISIBLE_STATES = [
  WORKFLOW_STATES.APPROVAZIONE_HQ,
  WORKFLOW_STATES.CHIUSO,
  WORKFLOW_STATES.RIFIUTATO_DITTA,
];

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const { user, userProfile, role } = useAuth();
  const [practices, setPractices] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [ditte, setDitte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    studioProgettazione: '',
    tipologiaLavorazione: '',
    ditta: '',
    workflowStatus: '',
    fornitoreIsInternalHq: '',
    categoriaRifiuto: '',
    minErrori: '',
    periodoDa: '',
    periodoA: '',
  });
  const [practicesLimitReached, setPracticesLimitReached] = useState(false);

  // Limite di sicurezza: evita di caricare dataset enormi in memoria
  const PRACTICES_LIMIT = 5000;

  // Classificazione territoriale del profilo Fastweb, estratta in primitive
  // stabili per l'array deps dell'effect (evita re-subscribe su ogni render).
  const fwIsEto = userProfile?.isEto === true;
  const fwAreeKey = Array.isArray(userProfile?.aree) ? userProfile.aree.join(',') : '';

  // Real-time listener sulle pratiche
  useEffect(() => {
    if (!firebaseConfigValid || !db || !user || !role) {
      setPractices([]);
      setLoading(false);
      return;
    }
    // Fornitore senza studioProgettazione: non caricare nulla (attendi profilo completo)
    if (role === ROLES.FORNITORE && !userProfile?.studioProgettazione) {
      setPractices([]);
      setLoading(true);
      return;
    }
    // Ditta senza dittaAppartenenza: non caricare nulla (attendi profilo completo)
    if (role === ROLES.DITTA && !userProfile?.dittaAppartenenza) {
      setPractices([]);
      setLoading(true);
      return;
    }

    let q;
    if (role === ROLES.FORNITORE && userProfile?.studioProgettazione) {
      q = query(collection(db, 'practices'),
        where('studioProgettazione', '==', userProfile.studioProgettazione),
        orderBy('createdAt', 'desc'),
        limit(PRACTICES_LIMIT));
    } else if (role === ROLES.DITTA && userProfile?.dittaAppartenenza) {
      // Le rules richiedono che il filtro workflowStatus sia esplicito nella query.
      // Indice composto: (ditta ASC, workflowStatus ASC, createdAt DESC).
      q = query(collection(db, 'practices'),
        where('ditta', '==', userProfile.dittaAppartenenza),
        where('workflowStatus', 'in', DITTA_VISIBLE_STATES),
        orderBy('createdAt', 'desc'),
        limit(PRACTICES_LIMIT));
    } else {
      q = query(collection(db, 'practices'),
        orderBy('createdAt', 'desc'),
        limit(PRACTICES_LIMIT));
    }

    setLoading(true);
    const unsub = onSnapshot(q, (snapshot) => {
      let data = snapshot.docs.map((d) => ({ _docId: d.id, ...d.data() }));
      setPracticesLimitReached(data.length >= PRACTICES_LIMIT);
      // Visibilita' territoriale Fastweb: gli utenti territoriali (aree sul
      // profilo, non ETO) vedono solo le pratiche della propria area (derivata
      // dalla provincia). Filtrando qui, a monte di setPractices, il perimetro
      // si propaga a Dashboard KPI, Attivita', Plots, Report ed export.
      if (role === ROLES.FASTWEB) {
        const fwProfile = { role, isEto: fwIsEto, aree: fwAreeKey ? fwAreeKey.split(',') : null };
        data = data.filter((p) => canSeePractice(fwProfile, p));
      }
      setPractices(data);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to practices:', err);
      setLoading(false);
    });
    return unsub;
  }, [user, role, userProfile?.studioProgettazione, userProfile?.dittaAppartenenza,
      // Ricarica il filtro territoriale se cambia la classificazione del profilo Fastweb
      fwIsEto, fwAreeKey]);

  // Real-time listener sull'anagrafica /ditte (popola dropdown PracticeForm).
  // Letta da qualsiasi utente autenticato (vedi firestore.rules).
  useEffect(() => {
    if (!firebaseConfigValid || !db || !user) {
      setDitte([]);
      return;
    }
    const unsub = onSnapshot(collection(db, 'ditte'), (snapshot) => {
      const data = snapshot.docs
        .map((d) => ({ _docId: d.id, ...d.data() }))
        .filter((d) => d.nome)
        .sort((a, b) => a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' }));
      setDitte(data);
    }, (err) => {
      console.error('Error listening to ditte:', err);
    });
    return unsub;
  }, [user]);

  const loadUploads = useCallback(async () => {
    if (!firebaseConfigValid || !db || !user) return;
    try {
      // Filtro per userId richiesto dalle firestore.rules: le list query
      // su /uploads devono essere restrittive per non triggerare
      // permission-denied sui ruoli non-HQ/Fastweb.
      const q = query(
        collection(db, 'uploads'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snap = await getDocs(q);
      setUploads(snap.docs.map((d) => ({ _docId: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading uploads:', e);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadUploads();
    } else {
      setUploads([]);
    }
  }, [user, loadUploads]);

  const uploadExcel = async (file, onProgress) => {
    if (!firebaseConfigValid || !db || !storage || !user) {
      throw new Error('Firebase non configurato');
    }
    const timestamp = Date.now();
    const storagePath = `uploads/${user.uid}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, storagePath);

    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, file);
      task.on('state_changed',
        (snap) => onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 50)),
        reject,
        resolve
      );
    });

    onProgress?.(55);
    const { valid, skipped, errors } = await parseExcelFile(file);
    onProgress?.(70);

    const uploadId = `${timestamp}_${file.name}`;
    const BATCH_SIZE = 500;

    let importedCount = 0;
    for (let i = 0; i < valid.length; i += BATCH_SIZE) {
      const chunk = valid.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);

      for (const practice of chunk) {
        const docData = {
          ...practice,
          sourceUploadId: uploadId,
          ...(userProfile?.studioProgettazione ? { studioProgettazione: userProfile.studioProgettazione } : {}),
          lastUpdatedBy: user.uid,
          updatedAt: serverTimestamp(),
        };
        const docRef = doc(db, 'practices', String(practice.id));
        const existing = practices.find((p) => p.id === practice.id);
        if (!existing) {
          docData.createdAt = serverTimestamp();
          docData.workflowStatus = WORKFLOW_STATES.NON_AVVIATO;
          docData.docCaricati = false;
          docData.createdByEmail = user.email;
        }
        batch.set(docRef, docData, { merge: true });
      }

      try {
        await batch.commit();
        importedCount += chunk.length;
      } catch (batchErr) {
        console.error(`Batch write failed at index ${i}:`, batchErr);
        throw new Error(`Importazione interrotta: ${importedCount} pratiche salvate su ${valid.length}. Errore: ${batchErr.message}`);
      }
      onProgress?.(70 + Math.round(((i + chunk.length) / valid.length) * 25));
    }

    const uploadDoc = {
      userId: user.uid,
      fileName: file.name,
      storagePath,
      createdAt: serverTimestamp(),
      totalRows: valid.length + skipped.length,
      importedRows: valid.length,
      skippedRows: skipped.length,
      errors: errors.slice(0, 50),
    };
    await setDoc(doc(db, 'uploads', uploadId), uploadDoc);
    writeAuditLog({
      userId: user.uid, userEmail: user.email,
      action: AUDIT_ACTIONS.EXCEL_UPLOADED,
      details: { fileName: file.name, imported: valid.length, skipped: skipped.length },
    });
    onProgress?.(100);

    await loadUploads();

    return { imported: valid.length, skipped: skipped.length, errors };
  };

  const getFileDownloadURL = async (storagePath) => {
    if (!storage) return null;
    return getDownloadURL(ref(storage, storagePath));
  };

  const addPractice = async (data) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    const existing = practices.find((p) => String(p.id) === String(data.id));
    if (existing) throw new Error(`ID ${data.id} già esistente`);

    const practice = {
      id: data.id,
      codiceSito: data.codiceSito || null,
      nomeSito: data.nomeSito || null,
      tipologiaLavorazione: data.tipologiaLavorazione || null,
      ditta: data.ditta || null,
      studioProgettazione: userProfile?.studioProgettazione || data.studioProgettazione || null,
      comune: data.comune || null,
      provincia: data.provincia || null,
      importo: data.importo,
      workflowStatus: WORKFLOW_STATES.NON_AVVIATO,
      docCaricati: false,
      // Frozen alla creazione: il controllore documenti (HQ o Fastweb) è determinato
      // dal profilo del fornitore al momento della creazione e non cambia più per
      // questa pratica, anche se il flag sul profilo utente viene modificato in seguito.
      fornitoreIsInternalHq: userProfile?.isInternalHq === true,
      createdByEmail: user.email,
      lastUpdatedBy: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      await setDoc(doc(db, 'practices', String(data.id)), practice);
    } catch (err) {
      if (err.code === 'permission-denied') {
        throw new Error(`ID ${data.id} già utilizzato da un altro studio. Scegli un ID diverso.`);
      }
      throw err;
    }
    writeAuditLog({ userId: user.uid, userEmail: user.email, action: AUDIT_ACTIONS.PRACTICE_CREATED, practiceId: String(data.id) });
    return practice;
  };

  const updatePractice = async (originalId, data) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');

    if (String(originalId) !== String(data.id)) {
      const dup = practices.find((p) => String(p.id) === String(data.id));
      if (dup) throw new Error(`ID ${data.id} già esistente`);
    }

    const existing = practices.find((p) => String(p.id) === String(originalId));
    const isRename = String(originalId) !== String(data.id);

    // Campi editabili dal form PracticeForm + metadati. Inviati sempre.
    // Allineato all'allowlist della rule Firestore "fornitore update base"
    // (fix Vuln 1 security-review): hasOnly([codiceSito, nomeSito,
    // tipologiaLavorazione, ditta, comune, provincia, importo, updatedAt,
    // lastUpdatedBy]). Un payload piu' ampio farebbe scattare la rule come
    // deny per pratiche legacy che non hanno tutti i campi preserved (es.
    // dataInizio assente → aggiunto come null → in affectedKeys → fuori
    // allowlist → permission-denied).
    const practice = {
      codiceSito: data.codiceSito || null,
      nomeSito: data.nomeSito || null,
      tipologiaLavorazione: data.tipologiaLavorazione || null,
      ditta: data.ditta || null,
      comune: data.comune || null,
      provincia: data.provincia || null,
      importo: data.importo,
      lastUpdatedBy: user.uid,
      updatedAt: serverTimestamp(),
    };

    // Rinominio ID: non e' un update, e' un create nuovo doc + delete vecchio.
    // La rule allow create richiede studioProgettazione e fornitoreIsInternalHq
    // coerenti col profilo; per non perdere i campi preserved (createdAt,
    // workflowStatus, ecc.) li includiamo solo in questo ramo.
    if (isRename && existing) {
      practice.id = data.id;
      practice.studioProgettazione = existing.studioProgettazione || null;
      practice.fornitoreIsInternalHq = existing.fornitoreIsInternalHq === true;
      practice.createdByEmail = existing.createdByEmail || null;
      practice.createdAt = existing.createdAt || null;
      practice.workflowStatus = existing.workflowStatus || 'non_avviato';
      practice.docCaricati = existing.docCaricati || false;
      if (existing.dataInizio) practice.dataInizio = existing.dataInizio;
    }

    await setDoc(doc(db, 'practices', String(data.id)), practice, { merge: true });
    if (isRename) {
      await deleteDoc(doc(db, 'practices', String(originalId)));
    }
    writeAuditLog({ userId: user.uid, userEmail: user.email, action: AUDIT_ACTIONS.PRACTICE_UPDATED, practiceId: String(data.id) });
    return practice;
  };

  const deletePractice = async (id) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    await deleteDoc(doc(db, 'practices', String(id)));
    writeAuditLog({ userId: user.uid, userEmail: user.email, action: AUDIT_ACTIONS.PRACTICE_DELETED, practiceId: String(id) });
  };

  const transitionPractice = async (practiceId, targetState, userRole, note, categorie) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    const practice = practices.find((p) => String(p.id) === String(practiceId));
    if (!practice) throw new Error(`Pratica ${practiceId} non trovata`);
    const currentState = practice.workflowStatus || WORKFLOW_STATES.NON_AVVIATO;
    if (!canTransition(currentState, targetState, userRole, practice)) {
      throw new Error(`Transizione non permessa: ${currentState} → ${targetState}`);
    }
    if (targetState === WORKFLOW_STATES.RIFIUTATO && (!note || !note.trim())) {
      throw new Error('Le note sono obbligatorie per il rifiuto');
    }
    const updates = {
      workflowStatus: targetState,
      lastUpdatedBy: user.uid,
      updatedAt: serverTimestamp(),
    };
    if (targetState === WORKFLOW_STATES.APPROVAZIONE_HQ) {
      // Approvazione documenti: avvia il timer "lavorazione documenti" (per KPI Tempo Medio HQ/Fastweb)
      // e contestualmente il timer "valutazione Ditta" (per KPI Tempo Medio Approvazione Ditta).
      updates.dataApprovazioneDocumenti = serverTimestamp();
      updates.dataInizioValutazioneDitta = serverTimestamp();
    }
    if (targetState === WORKFLOW_STATES.CHIUSO) {
      updates.dataFine = serverTimestamp();
    }
    if (targetState === WORKFLOW_STATES.NON_AVVIATO) {
      updates.docCaricati = false;
      // Reset flag idempotenza email per consentire re-invio al prossimo ciclo
      updates._emailSentDocCaricati = false;
      updates._emailSentRejected = false;
    }
    if (targetState === WORKFLOW_STATES.RIFIUTATO && note) {
      updates.noteRifiuto = note.trim();
      // Conteggio errori documenti del fornitore: ogni rifiuto conta come errore distinto.
      updates.numeroErroriDocumenti = increment(1);
      // Categorie del rifiuto (multi-selezione, almeno una — validata dalla modale).
      if (Array.isArray(categorie) && categorie.length > 0) {
        updates.categorieRifiuto = categorie;
      }
      // Apre il ciclo di rilavorazione dello studio: la CF onStudioRework lo chiude
      // (e consuma il campo) quando lo studio riclicca "Da caricare".
      updates.dataUltimoRifiuto = serverTimestamp();
    }
    await setDoc(doc(db, 'practices', String(practiceId)), updates, { merge: true });
    // Per le transizioni sui documenti (approvazione_hq/rifiutato), registriamo il
    // ruolo dell'attore: HQ di default, Fastweb per pratiche HQ-internal. Cosi' lo
    // Storico Operazioni puo' distinguere "approvato da HQ" vs "approvato da Fastweb".
    const isDocumentTransition =
      targetState === WORKFLOW_STATES.APPROVAZIONE_HQ || targetState === WORKFLOW_STATES.RIFIUTATO;
    writeAuditLog({
      userId: user.uid, userEmail: user.email,
      action: AUDIT_ACTIONS.PRACTICE_TRANSITION,
      practiceId: String(practiceId),
      details: {
        from: currentState,
        to: targetState,
        ...(note ? { noteRifiuto: note.trim() } : {}),
        ...(isDocumentTransition ? { actorRole: userRole } : {}),
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Transizioni dedicate per il ruolo Ditta e per l'aggiornamento preventivo.
  // Sono funzioni distinte (e non semplici wrapper di transitionPractice) perché
  // le firestore.rules per Ditta/Fornitore-aggiorna usano hasOnly() sui campi
  // permessi: serve scrivere ESATTAMENTE quei campi e niente altro.
  // ---------------------------------------------------------------------------

  const dittaApprovePreventivo = async (practiceId) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    const practice = practices.find((p) => String(p.id) === String(practiceId));
    if (!practice) throw new Error(`Pratica ${practiceId} non trovata`);
    if (practice.workflowStatus !== WORKFLOW_STATES.APPROVAZIONE_HQ) {
      throw new Error('Approvazione possibile solo dopo l\'approvazione documenti di HQ');
    }
    // Campi consentiti dalle rules: workflowStatus, dataFine, updatedAt, lastUpdatedBy
    await setDoc(doc(db, 'practices', String(practiceId)), {
      workflowStatus: WORKFLOW_STATES.CHIUSO,
      dataFine: serverTimestamp(),
      lastUpdatedBy: user.uid,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    writeAuditLog({
      userId: user.uid, userEmail: user.email,
      action: AUDIT_ACTIONS.PRACTICE_TRANSITION,
      practiceId: String(practiceId),
      details: { from: WORKFLOW_STATES.APPROVAZIONE_HQ, to: WORKFLOW_STATES.CHIUSO },
    });
  };

  const dittaRejectPreventivo = async (practiceId, note) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    if (!note || !note.trim()) {
      throw new Error('Le note sono obbligatorie per il rifiuto del preventivo');
    }
    const practice = practices.find((p) => String(p.id) === String(practiceId));
    if (!practice) throw new Error(`Pratica ${practiceId} non trovata`);
    if (practice.workflowStatus !== WORKFLOW_STATES.APPROVAZIONE_HQ) {
      throw new Error('Rifiuto possibile solo dopo l\'approvazione documenti di HQ');
    }
    const trimmed = note.trim();
    // Campi consentiti dalle rules: workflowStatus, noteRifiutoDitta, numeroErroriPreventivo,
    // dataUltimoRifiuto, updatedAt, lastUpdatedBy.
    // numeroErroriPreventivo: conteggio errori preventivo del fornitore — ogni rifiuto Ditta conta come errore distinto.
    // dataUltimoRifiuto: apre il ciclo di rilavorazione dello studio (chiuso dalla CF
    // onStudioRework quando il fornitore clicca "Aggiorna preventivo").
    await setDoc(doc(db, 'practices', String(practiceId)), {
      workflowStatus: WORKFLOW_STATES.RIFIUTATO_DITTA,
      noteRifiutoDitta: trimmed,
      numeroErroriPreventivo: increment(1),
      dataUltimoRifiuto: serverTimestamp(),
      lastUpdatedBy: user.uid,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    writeAuditLog({
      userId: user.uid, userEmail: user.email,
      action: AUDIT_ACTIONS.PRACTICE_TRANSITION,
      practiceId: String(practiceId),
      details: { from: WORKFLOW_STATES.APPROVAZIONE_HQ, to: WORKFLOW_STATES.RIFIUTATO_DITTA, noteRifiutoDitta: trimmed },
    });
  };

  const fornitoreAggiornaPreventivo = async (practiceId, newImporto) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    const importoNum = Number(newImporto);
    if (!Number.isFinite(importoNum) || importoNum < 0) {
      throw new Error('Importo non valido');
    }
    const practice = practices.find((p) => String(p.id) === String(practiceId));
    if (!practice) throw new Error(`Pratica ${practiceId} non trovata`);
    if (practice.workflowStatus !== WORKFLOW_STATES.RIFIUTATO_DITTA) {
      throw new Error('Aggiornamento preventivo possibile solo se la Ditta ha rifiutato');
    }
    // Campi consentiti dalle rules: workflowStatus, importo, dataInizioValutazioneDitta, updatedAt, lastUpdatedBy
    // dataApprovazioneDocumenti NON viene resettata: il controllore documenti non riapprova, salta il passaggio.
    // dataInizioValutazioneDitta VIENE riscritta: azzera il timer "valutazione Ditta" al nuovo round.
    await setDoc(doc(db, 'practices', String(practiceId)), {
      workflowStatus: WORKFLOW_STATES.APPROVAZIONE_HQ,
      importo: importoNum,
      dataInizioValutazioneDitta: serverTimestamp(),
      lastUpdatedBy: user.uid,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    writeAuditLog({
      userId: user.uid, userEmail: user.email,
      action: AUDIT_ACTIONS.PRACTICE_TRANSITION,
      practiceId: String(practiceId),
      details: {
        from: WORKFLOW_STATES.RIFIUTATO_DITTA,
        to: WORKFLOW_STATES.APPROVAZIONE_HQ,
        importoNuovo: importoNum,
        importoPrecedente: practice.importo ?? null,
      },
    });
  };

  // Fastweb prende in carico una pratica normale (non HQ-internal) come controllore
  // documenti. Setta inCaricoFastweb=true (frozen, mai true->false). HQ viene esclusa.
  // Rules: solo Fastweb, allowlist 3 campi, solo stati non_avviato/in_corso, solo se
  // non gia' presa in carico, solo se non HQ-internal.
  const setFastwebTakeover = async (practiceId) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    const practice = practices.find((p) => String(p.id) === String(practiceId));
    if (!practice) throw new Error(`Pratica ${practiceId} non trovata`);
    if (practice.inCaricoFastweb === true) {
      throw new Error('La pratica e\' gia\' presa in carico da Fastweb (azione irreversibile)');
    }
    if (practice.fornitoreIsInternalHq === true) {
      throw new Error('Le pratiche di fornitori HQ-internal sono gia\' di competenza Fastweb');
    }
    if (![WORKFLOW_STATES.NON_AVVIATO, WORKFLOW_STATES.IN_CORSO].includes(practice.workflowStatus)) {
      throw new Error('Presa in carico possibile solo per pratiche non avviate o in corso');
    }
    // Campi consentiti dalle rules: inCaricoFastweb, updatedAt, lastUpdatedBy
    await setDoc(doc(db, 'practices', String(practiceId)), {
      inCaricoFastweb: true,
      lastUpdatedBy: user.uid,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    writeAuditLog({
      userId: user.uid, userEmail: user.email,
      action: AUDIT_ACTIONS.FASTWEB_TAKEOVER,
      practiceId: String(practiceId),
      details: {
        workflowStatus: practice.workflowStatus,
        studioProgettazione: practice.studioProgettazione || null,
      },
    });
  };

  const setDocCaricati = async (practiceId, value) => {
    if (!firebaseConfigValid || !db || !user) throw new Error('Firebase non configurato');
    const practice = practices.find((p) => String(p.id) === String(practiceId));
    if (!practice) throw new Error(`Pratica ${practiceId} non trovata`);

    const updates = {
      docCaricati: value,
      lastUpdatedBy: user.uid,
      updatedAt: serverTimestamp(),
    };

    if (value && (!practice.workflowStatus || practice.workflowStatus === WORKFLOW_STATES.NON_AVVIATO)) {
      updates.workflowStatus = WORKFLOW_STATES.IN_CORSO;
      updates.dataInizio = serverTimestamp();
    }

    await setDoc(doc(db, 'practices', String(practiceId)), updates, { merge: true });
    writeAuditLog({
      userId: user.uid, userEmail: user.email,
      action: AUDIT_ACTIONS.DOC_CARICATI,
      practiceId: String(practiceId),
      details: { docCaricati: value, autoTransition: !!updates.workflowStatus },
    });
  };

  const getFilteredPractices = useCallback(() => {
    return filterPractices(practices, filters);
  }, [practices, filters]);

  const uniqueStudi = useMemo(() => {
    const set = new Set();
    practices.forEach((p) => { if (p.studioProgettazione) set.add(p.studioProgettazione); });
    return Array.from(set).sort();
  }, [practices]);

  const uniqueDitte = useMemo(() => {
    const set = new Set();
    practices.forEach((p) => { if (p.ditta) set.add(p.ditta); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
  }, [practices]);

  useEffect(() => {
    if (filters.studioProgettazione && !uniqueStudi.includes(filters.studioProgettazione)) {
      setFilters((prev) => ({ ...prev, studioProgettazione: '' }));
    }
  }, [uniqueStudi, filters.studioProgettazione]);

  useEffect(() => {
    if (filters.ditta && !uniqueDitte.includes(filters.ditta)) {
      setFilters((prev) => ({ ...prev, ditta: '' }));
    }
  }, [uniqueDitte, filters.ditta]);

  return (
    <DataContext.Provider value={{
      practices, loading, filters, setFilters,
      getFilteredPractices, uploadExcel, uploads,
      addPractice, updatePractice, deletePractice, transitionPractice, setDocCaricati,
      dittaApprovePreventivo, dittaRejectPreventivo, fornitoreAggiornaPreventivo,
      setFastwebTakeover,
      getFileDownloadURL, uniqueStudi, uniqueDitte, ditte, practicesLimitReached,
      refreshData: () => { loadUploads(); },
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
